// @ts-nocheck
import { applyTheme } from '@cloudscape-design/components/theming';
import {
  colorChartsBlue11000,
  colorChartsBlue1300,
  colorChartsGreen300,
  colorChartsOrange1200,
  colorChartsOrange700,
} from '@cloudscape-design/design-tokens';

// Define color constants
const colorOrange600 = '#E65E2D';
const colorOrange100 = '#FFE1D6';
const colorOrange800 = '#CC5328';
const colorTeal600 = '#4CA2A6';
const colorOrange500 = '#FF6832';
const colorOrange400 = '#FF865B';
const colorOrange300 = '#FFA484';
const colorOrange200 = '#FFC3AD';
const colorGrey800 = '#0F1B2A';
const colorGrey750 = '#192534';
const colorGrey700 = '#232F3E';
const colorGrey650 = '#354150';
const colorTeal500 = '#59BFC3';
const colorTeal100 = '#D2F3F4';
const colorGreen600 = '#5F9619';
const colorGrey600 = '#414D5C';
const colorGreen500 = '#78BE20';
const colorGrey550 = '#5F6B7A';
const colorGrey500 = '#7D8998';
const colorGrey450 = '#8D99A8';
const colorGrey400 = '#9BA7B6';
const colorGrey300 = '#D1D5DB';
const colorGrey200 = '#E9EBED';
const colorGrey150 = '#F4F4F4';
const colorGrey125 = '#F8F8F8';
const colorGrey100 = '#FBFBFB';
const colorWhite = '#FFFFFF';
const colorTestRed = '#FF0000';

// Define font constants
const fontLatoRegular = 'Lato, sans-serif';
const fontGothamBold = 'GothamSSm-Bold, Arial, sans-serif';
const fontGothamBlack = 'GothamSSm-Black, Arial, sans-serif';
const fontGothamBlackItalic = 'GothamSSm-BlackItalic, Arial, sans-serif';
const fontGothamBoldItalic = 'GothamSSm-BoldItalic, Arial, sans-serif';
const fontGothamBook = 'GothamSSm-Book, Arial, sans-serif';
const fontGothamBookItalic = 'GothamSSm-BookItalic, Arial, sans-serif';
const fontGothamLight = 'GothamSSm-Light, Arial, sans-serif';
const fontGothamLightItalic = 'GothamSSm-LightItalic, Arial, sans-serif';
const fontGothamMedium = 'GothamSSm-Medium, Arial, sans-serif';
const fontGothamMediumItalic = 'GothamSSm-MediumItalic, Arial, sans-serif';
const fontGothamXLight = 'GothamSSm-XLight, Arial, sans-serif';
const fontGothamXLightItalic = 'GothamSSm-XLightItalic, Arial, sans-serif';
const fontLatoBold = 'Lato-Bold, sans-serif';
const fontLatoBoldItalic = 'Lato-BoldItalic, sans-serif';
const fontLatoRegularItalic = 'Lato-RegularItalic, sans-serif';

const theme = {
  tokens: {
    colorTextBreadcrumbCurrent: colorChartsBlue11000,
    colorTextLayoutToggle: colorGrey750,
    colorBackgroundLayoutToggleDefault: colorWhite,
    colorBackgroundLayoutToggleActive: colorWhite,
    colorBackgroundLayoutToggleHover: colorGrey300,
    colorBackgroundLayoutToggleSelected: colorWhite,
    /*     --color-background-layout-main-nx09lr: #ffffff;
    --color-background-layout-mobile-panel-0ussje: #000716;
    --color-background-layout-panel-content-t82vgh: #FBFBFB;
    --color-background-layout-panel-hover-n9apd3: #e9ebed;
    --color-background-layout-toggle-active-robi8g: #354150;
    --color-background-layout-toggle-default-lmdlhy: #FFFFFF;
    --color-background-layout-toggle-hover-ho9150: #354150;
    --color-background-layout-toggle-selected-active-xfk480: #0972d3;
    --color-background-layout-toggle-selected-default-caco0q: #E65E2D;
    --color-background-layout-toggle-selected-hover-kjak2q: #065299; */
    colorBackgroundLayoutMobilePanelContent: colorTestRed,
    // datepicker efforts above
    colorBackgroundContainerHeader: colorGrey200, //our table header color
    colorBackgroundContainerContent: colorGrey100, // used by our split panel bottom
    colorTextEmpty: colorTeal100,
    colorTextStatusInactive: colorTeal500,
    colorTextAccent: colorOrange600,
    colorBackgroundControlChecked: colorOrange600,
    colorBackgroundItemSelected: colorOrange100,
    colorBorderItemSelected: colorOrange600,
    colorBorderButtonNormalDefault: colorOrange600,
    colorBorderButtonNormalActive: colorOrange600,
    colorBorderButtonNormalHover: colorOrange100,
    colorBackgroundButtonNormalActive: colorOrange100,
    colorBackgroundButtonNormalDefault: colorOrange600,
    colorBackgroundButtonNormalDisabled: colorOrange100,
    colorBackgroundButtonNormalHover: colorOrange100,
    colorBackgroundButtonPrimaryActive: colorOrange100,
    colorBackgroundButtonPrimaryDefault: colorOrange600,
    colorBackgroundButtonPrimaryDisabled: colorOrange100,
    colorBackgroundButtonPrimaryHover: colorOrange100,
    colorTextInteractiveActive: colorOrange600,
    colorTextInteractiveHover: colorOrange100,
    colorTextButtonNormalActive: colorOrange600,
    colorTextButtonNormalDefault: colorOrange100,
    colorTextButtonNormalHover: colorOrange600,
    colorTextButtonPrimaryActive: colorWhite,
    colorTextButtonPrimaryDefault: colorOrange100,
    colorTextButtonPrimaryHover: colorOrange600,
    colorTextLinkDefault: colorOrange600,
    colorTextLinkHover: colorOrange100,
    colorBackgroundCellShaded: colorOrange100,
    colorBackgroundLayoutToggleSelectedDefault: colorOrange600,

    // Typography tokens
    fontFamilyBase: `${fontGothamMedium}, ${fontLatoRegular}, 'Arial', sans-serif`,
    fontFamilyMonospace: `GothamSSm-Bold, Monaco, Menlo, Consolas, 'Courier Prime', Courier, 'Courier New', monospace`,
    fontSizeHeadingXl: `24px`,
    fontSizeBodyM: '14px',
    fontSizeBodyS: '12px',
    fontSizeDisplayL: '42px',
    fontSizeHeadingL: '20px',
    fontSizeHeadingM: '18px',
    fontSizeHeadingS: '16px',
    fontSizeHeadingXs: '14px',
    fontWeightHeadingXl: '700',
    fontWeightHeadingL: '700',
    fontWeightHeadingM: '700',
    fontWeightHeadingS: '700',
    fontWeightHeadingXs: '700',
  },
  contexts: {
    'top-navigation': {
      tokens: {
        colorBackgroundLayoutToggleDefault: colorWhite,
        colorBackgroundLayoutToggleActive: colorWhite,
        colorBackgroundLayoutToggleHover: colorWhite,
        colorBackgroundLayoutToggleSelected: colorWhite,
        // above are the toggle butons
        colorTextInteractiveDefault: colorWhite, //topnav email address color
        colorTextInteractiveHover: colorGrey100, //topnav email address color
        /*         colorBackgroundContainerHeader: colorGrey300, */
        colorBackgroundContainerContent: colorGrey800, // topNavBar
        colorTextAccent: colorOrange100,
        colorTextInteractiveActive: colorOrange100,

        colorTextLayoutToggleSelected: colorOrange600,
        colorTextLinkDefault: colorOrange600,
        colorTextLinkHover: colorOrange100,
        colorBackgroundButtonNormalDefault: colorOrange100,
        colorBackgroundButtonNormalHover: colorOrange600,
        colorBorderButtonNormalDefault: colorOrange100,
        colorBorderButtonNormalHover: colorOrange600,
        colorTextLinkButtonNormalDefault: colorOrange100,
        colorTextLinkButtonNormalHover: colorOrange100,
        colorTextLinkButtonNormalActive: colorOrange600,
        colorTextButtonNormalDefault: colorOrange100,
        colorTextButtonNormalHover: colorOrange100,
        colorTextButtonPrimaryDefault: colorOrange100,
        colorTextButtonPrimaryHover: colorOrange100,
        colorBackgroundCellShaded: colorOrange100,
        colorTextDropdownItemHighlighted: colorOrange100,
      },
    },
    header: {
      tokens: {
        colorTextBreadcrumbIcon: colorChartsBlue11000,
        colorTextBreadcrumbCurrent: colorChartsBlue11000,

        colorBackgroundItemSelected: colorWhite,
        colorBackgroundControlChecked: colorGreen500,
        colorBackgroundContainerContent: colorGrey125,
        // datepicker efforts above

        colorTextButtonPrimaryDefault: colorWhite,
        colorTextButtonPrimaryActive: colorWhite,
        colorTextBodyDefault: colorGrey800, // search input font
        colorTextFormDefault: colorGrey500, // search return summary
        colorBackgroundInputDefault: colorWhite, ///search box background
        colorTextStatusInactive: colorGreen600, // loading text font
        colorBackgroundLayoutMain: colorGrey150,
        colorTextInteractiveActive: colorOrange600,
        colorTextInteractiveHover: colorOrange600,
        colorTextLinkDefault: colorOrange600,
        colorTextLinkHover: colorGrey600,
        colorTextHeadingDefault: colorGrey800,
        colorTextLinkButtonNormalDefault: colorOrange100,
        colorTextLinkButtonNormalHover: colorOrange100,
        colorTextLinkButtonNormalActive: colorOrange600,
        colorTextButtonNormalDefault: colorOrange600, //reports button
        colorTextButtonNormalHover: colorGrey100, //reports button
        colorBackgroundButtonNormalDefault: colorWhite,
        colorBackgroundButtonNormalActive: colorWhite,
        colorBackgroundButtonNormalDisabled: colorWhite,
        colorBackgroundButtonNormalHover: colorOrange600,
        colorTextButtonNormalActive: colorOrange600,
        colorBorderButtonNormalDefault: colorOrange600,
        colorBorderButtonNormalActive: colorOrange600,
        colorBorderButtonNormalHover: colorOrange100,
        colorBorderItemSelected: colorOrange600,
      },
    },
  },
};

// Ensure to pass the theme object with a 'theme' key
const { reset } = applyTheme({ theme });

// Export reset if necessary, or use it to remove the theme later
export { theme, reset };
